import {
  Equal,
  IQueryCommandOptions,
  ISearchCommandOptions,
  IWhereCommand,
  Not,
  Null,
  convertQueryCommandToQueryString,
} from '@llws/dynamic-router-utils';
import {
  ISuiteTypes,
} from '@llws/lift-entity-interfaces';
import dayjs from 'dayjs';

import {
  CurrentUser,
  IApiResponse,
  IExportFilterPayload,
  IExportFilterPayloadV2,
  IMetaResponse,
  IModifyPayload,
  mergeQueryCommandOptionsV2,
} from '@/shared';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { downloadCSV } from '@/shared/helpers/download-csv';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';

// Base store configuration
const adminGatewayResourcePath = '/property-api/units';
const listViewName: string = '';
const showLogging: boolean = false;

interface ISuiteTypeExtraData extends ISuiteTypes {
  numberNonDeletedUnits?: null | number,
}

const defaultGetListFilters: IQueryCommandOptions = {
  alias: 'um',
  relations: [
    {
      path: 'um.building',
      alias: 'building',
    },
    {
      path: 'building.city',
      alias: 'city',
    },
    {
      path: 'city.province',
      alias: 'province',
    },
    {
      path: 'um.suitesHasPhotos',
      alias: 'shp',
    },
    {
      path: 'um.suiteTypesFloorplans',
      alias: 'stf',
    },
    {
      path: 'shp.listingPhoto',
      alias: 'shplp',
    },
  ],
  where: [
    Not(Equal('um.status', 'deleted')),
    Not(Equal('building.status', 'deleted')),
    Null('um.suiteTypeModelId'),
  ],

};

const defaultExportFields: Array<Record<string, string>> = [
  {
    display: 'Client ID',
    property: 'clientId',
  },
  {
    display: 'Property ID',
    property: 'buildingId',
  },
  {
    display: 'Property Name',
    property: 'building.buildingName',
    csvCustomTemplate: '{building.buildingName}',
  },
  {
    display: 'Property Address',
    property: 'building',
    csvCustomTemplate: '{building.streetNumber}, {building.streetName}',
  },
  {
    display: 'Unit ID',
    property: 'id',
  },
  {
    display: 'Unit Name',
    property: 'typeName',
  },
  {
    display: 'Unit #',
    property: 'number',
  },
  {
    display: 'Floor #',
    property: 'floor',
  },
  {
    display: 'Rent',
    property: 'rate',
  },
  {
    display: 'Bedrooms',
    property: 'bed',
  },
  {
    display: 'Bathrooms',
    property: 'bath',
  },
  {
    display: 'Den',
    property: 'den',
  },
  {
    display: 'Square Feet',
    property: 'sqFt',
  },
  {
    display: 'Deposit',
    property: 'deposit',
  },
  {
    display: 'Availability',
    property: 'available',
  },
  {
    display: 'Status',
    property: 'status',
  },
  {
    display: 'Hydro Rate',
    property: 'hydroRate',
  },
  {
    display: 'Lease Term',
    property: 'leaseTerm',
  },
  {
    display: 'Parking Spots Count',
    property: 'parkingSpotsCount',
  },
  {
    display: 'Parking Spot Parallel',
    property: 'parkingSpotsParallel',
  },
  {
    display: 'Parking Spot Tandem',
    property: 'parkingSpotsTandem',
  },
  {
    display: '# of Storage Spaces',
    property: 'storageSpotsCount',
  },
  {
    display: 'Storage Space Rate',
    property: 'storageSpaceRate',
  },
  {
    display: 'Furnished Feature',
    property: 'furnished',
  },
  {
    display: 'Semi Furnished Feature',
    property: 'semiFurnished',
  },
  {
    display: 'Luxury Feature',
    property: 'luxury',
  },
  {
    display: 'Executive Feature',
    property: 'executive',
  },
  {
    display: 'Waterfront Feature',
    property: 'waterfront',
  },
];

const simpleGetListFilters: IQueryCommandOptions = {
  where: [
    Not(Equal('status', 'deleted')),
    Null('suiteTypeModelId'),
  ],
  limit: 999999,
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  alias: 'um',
  relations: [
    {
      path: 'suiteTypeCommercialFields',
      alias: 'stcf',
    },
    {
      path: 'suiteTypesFloorplans',
      alias: 'stf',
    },
    {
      path: 'suitesHasPhotos',
      alias: 'shp',
    },
    {
      path: 'shp.listingPhoto',
      alias: 'shplp',
    },
  ],
};

interface ISuiteTypesState {
  simpleList: Array<ISuiteTypeExtraData>,
}

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ISuiteTypeExtraData>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'unit-models/'

  state: {
    ...base.state,
    simpleList: [] as Array<ISuiteTypeExtraData>,
  },

  getters: {
    ...base.getters,

    /** Searches local unit state using a single parameter */
    searchedList(state: IStateStrict<ISuiteTypeExtraData>) {
      return (searchValue: string) => {
        if (!state.list?.data?.length) {
          return [];
        }

        if (!searchValue.trim()) {
          return state.list?.data ?? [];
        }

        const lowerCaseVal = searchValue.toLowerCase();

        return state.list.data.filter((item: ISuiteTypeExtraData) => ([
          item.typeName,
          item.number ?? '',
          item.status,
        ].join('').trim().includes(lowerCaseVal)));
      };
    },

    // Override the getter 'list' from the base implementation
    list(state: IStateStrict<ISuiteTypeExtraData>) {
      if (state.list?.data) {
        return state.list.data.map((l: ISuiteTypeExtraData) => ({
          ...l,
          ...{
            suitesHasPhotos: (l.suitesHasPhotos!)
              .filter((v) => v.listingPhoto?.active === 1 && v.listingPhoto?.buildingId === l.buildingId)
              .sort((a, b) => (a.orderBy > b.orderBy ? 1 : -1)),
          },
        }));
      }

      return [];
    },

    // Override the getter 'single' from the base implementation
    single(state: IStateStrict<ISuiteTypeExtraData>) {
      if (state.single) {
        const suitesHasPhotos = (state.single.data?.suitesHasPhotos ?? [])
          .filter((v) => v.listingPhoto?.active === 1 && v.listingPhoto?.buildingId === state.single?.data?.buildingId)
          .sort((a, b) => (a.orderBy > b.orderBy ? 1 : -1));

        const suiteTypesFloorplans = (state.single.data?.suiteTypesFloorplans ?? [])
          .sort((a, b) => (a.orderBy > b.orderBy ? 1 : -1));

        return {
          ...state.single.data!,
          suitesHasPhotos,
          suiteTypesFloorplans,
        } as ISuiteTypeExtraData;
      }

      return undefined;
    },

    simpleList(state: ISuiteTypesState) {
      return state.simpleList;
    },
  },

  mutations: {
    ...base.mutations,
    setSimpleList(state: ISuiteTypesState, payload: Array<ISuiteTypeExtraData>) {
      state.simpleList = payload;
    },
  },

  actions: {
    ...base.actions,
    async getByLegacySetting(ctx: IAction<IStateStrict<ISuiteTypeExtraData>>, payload: IFilterPayloadV2) {
      if ((ctx.rootGetters['users/current'] as CurrentUser).getClientSettingValue('suite_types_legacy') === '1') {
        base.defaultGetListFilters.where = (base.defaultGetListFilters.where || []).filter(
          (w) => (w as IWhereCommand).field !== 'suiteTypeModelId',
        );
      }
      await ctx.dispatch('get', payload);
    },

    async searchByLegacySetting(ctx: IAction<IStateStrict<ISuiteTypeExtraData>>, payload: ISearchCommandOptions) {
      if ((ctx.rootGetters['users/current'] as CurrentUser).getClientSettingValue('suite_types_legacy') === '1') {
        base.defaultGetListFilters.where = (base.defaultGetListFilters.where || []).filter(
          (w) => (w as IWhereCommand).field !== 'suiteTypeModelId',
        );
      }
      await ctx.dispatch('search', payload);
    },

    async search(
      ctx: IAction<IStateStrict<ISuiteTypeExtraData>>,
      payload: ISearchCommandOptions & { showNonPrimary?: string, buildingId?: number },
    ): Promise<IApiResponse<Partial<ISuiteTypeExtraData>, IMetaResponse>> {
      const mergedCommandOptions = mergeQueryCommandOptionsV2([base.defaultGetListFilters, (payload || {})]);
      const result: IApiResponse<Partial<ISuiteTypeExtraData>, IMetaResponse> = await adminRequest
        .post(`/v1/property-api/suite_types/_search?show_non_primary=${payload.showNonPrimary}`, mergedCommandOptions);

      ctx.commit('set', result);
      return result;
    },

    async duplicateUnit(
      ctx: IAction<IStateStrict<ISuiteTypeExtraData>>,
      payload: IModifyPayload & { duplicateMediaUnitId?: string, duplicateMediaType?: number },
    ): Promise<IApiResponse<Partial<ISuiteTypeExtraData>, IMetaResponse>> {
      const result: IApiResponse<Partial<ISuiteTypeExtraData>, IMetaResponse> = await adminRequest
        .post(`/v1/property-api/duplicateUnit?duplicateMediaUnitId=${payload.additional?.duplicateMediaUnitId}&duplicateMediaType=${payload.additional?.duplicateMediaType}`, payload.data);

      ctx.commit('set', result);
      return result;
    },

    getSimpleList: async (ctx: IAction<IStateStrict<ISuiteTypeExtraData>>, payload: IFilterPayloadV2) => {
      const result = await adminRequest.get(adminGatewayResourcePath, convertQueryCommandToQueryString(
        mergeQueryCommandOptionsV2([simpleGetListFilters, payload.filters ?? {}]),
      ));
      ctx.commit('setSimpleList', result.data);
    },

    async getCSV(
      ctx: IAction<IStateStrict<ISuiteTypes>>,
      payload: IExportFilterPayloadV2,
    ) {
      const mergedCommandOptions = mergeQueryCommandOptionsV2([base.defaultGetListFilters, (payload.filters || {})]);
      const result: IApiResponse<Partial<ISuiteTypes>, IMetaResponse> = await adminRequest.post(
        '/v1/property-api/suite_types/_search?csvExport=SAME_METHOD',
        {
          ...mergedCommandOptions,
          limit: ctx.state.list?.meta?.totalRecords ?? 999,
          page: 1,
          fields: defaultExportFields,
        },
      );

      downloadCSV(result as string, `${dayjs().format('YYYY-MM-DD')}_unit_list.csv`);
    },
  },
};
